// The transcript owns message adjacency. Consecutive messages from one
// sender share the first message's identity and the last message's actions.
import { createContext, useContext } from 'react';

import {
    type MessageSender,
    type ProjectedTranscriptRow,
} from '@apps/web/features/phoebe_chat_v3/registry/types';

export type MessageContinuation = Readonly<{
    /** A message from the same sender is directly above this one. */
    readonly continuesPrevious: boolean;
    /** A message from the same sender directly follows this one. */
    readonly continuedByNext: boolean;
    /** A card follows this text before the next message. */
    readonly followedByCard?: boolean;
}>;

export const NO_MESSAGE_CONTINUATION: MessageContinuation = {
    continuesPrevious: false,
    continuedByNext: false,
};

export const MessageContinuationContext = createContext<MessageContinuation>(
    NO_MESSAGE_CONTINUATION,
);

export function useMessageContinuation(): MessageContinuation {
    return useContext(MessageContinuationContext);
}

export function messageSenderOf(
    row: ProjectedTranscriptRow | undefined,
): MessageSender | null {
    return row?.kind === 'occurrence' ? (row.messageSender ?? null) : null;
}

function sameSender(
    row: ProjectedTranscriptRow | undefined,
    other: ProjectedTranscriptRow | undefined,
): boolean {
    const sender = messageSenderOf(row);
    return sender !== null && sender === messageSenderOf(other);
}

// One shared value per combination keeps the row context referentially
// stable across renders, so rows do not re-render for an unchanged place.
const CONTINUATION_VALUES: Readonly<
    Record<'nn' | 'ny' | 'yn' | 'yy', MessageContinuation>
> = {
    nn: NO_MESSAGE_CONTINUATION,
    ny: { continuesPrevious: false, continuedByNext: true },
    yn: { continuesPrevious: true, continuedByNext: false },
    yy: { continuesPrevious: true, continuedByNext: true },
};

export function continuationOf(
    rows: ReadonlyArray<ProjectedTranscriptRow>,
    index: number,
): MessageContinuation {
    const row = rows[index];
    const continuesPrevious = sameSender(rows[index - 1], row);
    const continuedByNext = sameSender(row, rows[index + 1]);
    const continuation =
        CONTINUATION_VALUES[
            `${continuesPrevious ? 'y' : 'n'}${continuedByNext ? 'y' : 'n'}`
        ];
    if (messageSenderOf(row) === 'assistant') {
        for (
            let nextIndex = index + 1;
            nextIndex < rows.length;
            nextIndex += 1
        ) {
            const next = rows[nextIndex]!;
            if (next.kind === 'day_heading' || messageSenderOf(next) !== null) {
                break;
            }
            if (next.kind === 'occurrence' && next.isCard === true) {
                return { ...continuation, followedByCard: true };
            }
        }
    }
    return continuation;
}

export type AssistantTurnPosition =
    | Readonly<{ readonly kind: 'interim' }>
    | Readonly<{ readonly kind: 'end'; readonly copyText: string }>;

const INTERIM_TURN_POSITION: AssistantTurnPosition = { kind: 'interim' };

export const AssistantTurnPositionContext =
    createContext<AssistantTurnPosition | null>(null);

export function useAssistantTurnPosition(): AssistantTurnPosition | null {
    return useContext(AssistantTurnPositionContext);
}

export function assistantTurnPositions(
    rows: ReadonlyArray<ProjectedTranscriptRow>,
): ReadonlyMap<string, AssistantTurnPosition> {
    const positions = new Map<string, AssistantTurnPosition>();
    let turnEndRowId: string | null = null;
    let turnTexts: Array<string> = [];
    const closeTurn = () => {
        if (turnEndRowId !== null) {
            positions.set(turnEndRowId, {
                kind: 'end',
                copyText: turnTexts.join('\n\n'),
            });
        }
        turnEndRowId = null;
        turnTexts = [];
    };
    for (const row of rows) {
        const sender = messageSenderOf(row);
        if (sender === 'assistant' && row.kind === 'occurrence') {
            if (turnEndRowId !== null) {
                positions.set(turnEndRowId, INTERIM_TURN_POSITION);
            }
            turnEndRowId = row.rowId;
            if (row.messageText !== undefined) {
                turnTexts.push(row.messageText);
            }
        } else if (sender?.startsWith('user:') === true) {
            closeTurn();
        }
    }
    closeTurn();
    return positions;
}
